import type { Ref } from 'vue';

import { computed, ref } from 'vue';

export interface JobCarouselItem {
  jobId: number;
  jobDate: string;
}

export interface UseJobCarouselReturn {
  jobs: Ref<JobCarouselItem[]>;
  currentIndex: Ref<number>;
  currentJob: Ref<JobCarouselItem | null>;
  previousJob: Ref<JobCarouselItem | null>;
  nextJob: Ref<JobCarouselItem | null>;
  canNavigate: Ref<boolean>;
  totalJobs: Ref<number>;
  position: Ref<string>;
  setJobs: (newJobs: JobCarouselItem[], startIndex?: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  goToIndex: (index: number) => void;
  reset: () => void;
}

export default function useJobCarousel(): UseJobCarouselReturn {
  const jobs = ref<JobCarouselItem[]>([]);
  const currentIndex = ref<number>(0);

  const currentJob = computed<JobCarouselItem | null>(() => {
    if (jobs.value.length === 0 || currentIndex.value < 0) {
      return null;
    }
    return jobs.value[currentIndex.value] || null;
  });

  const canNavigate = computed<boolean>(() => {
    return jobs.value.length > 1;
  });

  const previousJob = computed<JobCarouselItem | null>(() => {
    if (!canNavigate.value) return null;
    const len = jobs.value.length;
    return jobs.value[(currentIndex.value - 1 + len) % len] || null;
  });

  const nextJob = computed<JobCarouselItem | null>(() => {
    if (!canNavigate.value) return null;
    const len = jobs.value.length;
    return jobs.value[(currentIndex.value + 1) % len] || null;
  });

  const totalJobs = computed<number>(() => {
    return jobs.value.length;
  });

  const position = computed<string>(() => {
    if (jobs.value.length === 0) {
      return '';
    }
    return `Job ${currentIndex.value + 1} of ${jobs.value.length}`;
  });

  function setJobs(newJobs: JobCarouselItem[], startIndex = 0): void {
    jobs.value = [...newJobs];
    currentIndex.value = Math.max(0, Math.min(startIndex, newJobs.length - 1));
  }

  function goToNext(): void {
    if (!canNavigate.value) return;

    const len = jobs.value.length;
    currentIndex.value = (currentIndex.value + 1) % len;
  }

  function goToPrevious(): void {
    if (!canNavigate.value) return;

    const len = jobs.value.length;
    currentIndex.value = (currentIndex.value - 1 + len) % len;
  }

  function goToIndex(index: number): void {
    if (index < 0 || index >= jobs.value.length) return;

    currentIndex.value = index;
  }

  function reset(): void {
    jobs.value = [];
    currentIndex.value = 0;
  }

  return {
    jobs,
    currentIndex,
    currentJob,
    previousJob,
    nextJob,
    canNavigate,
    totalJobs,
    position,
    setJobs,
    goToNext,
    goToPrevious,
    goToIndex,
    reset,
  };
}
